import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as IconCheck } from "../../styles/assets/svg/icon_checked.svg";
import { ReactComponent as IconCircleCheck } from "../../styles/assets/svg/icon_circle_check.svg";
import * as S from "./Register.style";
import { PageContainer } from "src/components/organisms/contents";
import { ModalRegister } from "src/components/molecules/ModalRegister";
import { Terms } from "./components/Terms";

type Term = {
  id: string;
  label: string;
  required: boolean;
  detailPath?: string; // 약관 상세 이동 경로
};

const TERMS: Term[] = [
  {
    id: "personal",
    label: "[필수] 개인정보 수집 및 이용 동의",
    required: true,
  },
  { id: "health", label: "[필수] 건강정보 처리 및 분석 동의", required: true },
  { id: "mydata", label: "[필수] 마이데이터 기반 활용 동의", required: true },
  { id: "mkt", label: "[선택] 마케팅 수신 동의", required: false },
];

export const RegisterPage = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const { reg } = useParams();

  const [activeMap, setActiveMap] = useState<Record<string, boolean>>(
    Object.fromEntries(TERMS.map((t) => [t.id, false]))
  );

  const [modalShow, setModalShow] = useState<boolean>(false);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);

  const navi = useNavigate();
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 모두 체크 유무 확인
  const isAllActive = Object.values(activeMap).every(Boolean); // 현재 전부 체크되어 있는지 확인

  // 필수 체크 유무 확인
  const isRequiredActive = TERMS.filter((t) => t.required).every(
    (t) => activeMap[t.id]
  );

  const toggleActive = (id: string) => {
    setActiveMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAllActive = () => {
    const newValue = !isAllActive;
    setActiveMap(Object.fromEntries(TERMS.map((t) => [t.id, newValue])));
  };

  const setActiveTrue = (id: string) => {
    setActiveMap((prev) => ({ ...prev, [id]: true }));
  };

  const selectedTerm = TERMS.find((t) => t.id === selectedTermId);

  return (
    <PageContainer isNavBar={false} active={null}>
      {/* Header */}
      <>
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "rgba(247,247,247,0.92)",
            backdropFilter: "blur(3px)",
            padding: `16px 0`,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <button
            aria-label="back"
            onClick={() => navi(-1)}
            style={{
              background: "transparent",
              border: "none",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            <svg width={22} height={22} viewBox="0 0 24 24">
              <path
                d="M19 12H5M5 12L12 5M5 12L12 19"
                stroke="#1F2937"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            {reg === "join" ? "회원가입" : "정보입력"}
          </div>
          <div style={{ width: 22, height: 22 }} />
        </div>

        {/* 하단 버튼 */}
        <div
          data-layer="Btm_Buttons"
          style={{
            width: "100%",
            maxWidth: "45rem",
            padding: isMobile ? 12 : 16,
            bottom: 0,
            position: "fixed",
            background: "white",
            borderTop: "1px rgba(0, 0, 0, 0.05) solid",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: 10,
            display: "inline-flex",
            boxSizing: "border-box",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <button
            style={{
              alignSelf: "stretch",
              height: isMobile ? 44 : 48,
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 8,
              paddingBottom: 8,
              background: isRequiredActive ? "#F57C00" : "#FFD9B3",
              borderRadius: 8,
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              display: "inline-flex",
              border: "none",
              cursor: "pointer",
              fontSize: isMobile ? 14 : 16,
              fontWeight: "500",
              color: "white",
            }}
            onClick={() => {
              if (isRequiredActive && reg) {
                navi("/register/userInfo/join");
              } else {
                navi("/register/userInfo");
              }
            }}
          >
            다음
          </button>
        </div>

        {/* 메인 콘텐츠 */}
        <div
          style={{
            width: "100%",
            paddingTop: "16px",
            paddingBottom: isMobile ? 80 : 100,
            position: "relative",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: 24,
            display: "inline-flex",
            boxSizing: "border-box",
          }}
        >
          {/* 제목 섹션 */}
          <div
            style={{
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 8,
              display: "flex",
            }}
          >
            <div
              style={{
                color: "#737373",
                fontSize: "18px",
                fontFamily: "Pretendard",
                fontWeight: "500",
                lineHeight: isMobile ? "24px" : "28px",
                wordWrap: "break-word",
                textAlign: "center",
                alignSelf: "flex-start",
              }}
            >
              1/2
            </div>
            <h1
              style={{
                color: "#404040",
                fontSize: "24px",
                fontFamily: "Pretendard",
                fontWeight: "700",
                lineHeight: isMobile ? "28px" : "32px",
                wordWrap: "break-word",
                margin: 0,
              }}
            >
              루틴케어, 시작해볼까요?
            </h1>
            <p
              style={{
                color: "#737373",
                fontSize: "16px",
                fontFamily: "Pretendard",
                fontWeight: "500",
                lineHeight: isMobile ? "20px" : "24px",
                wordWrap: "break-word",
                margin: 0,
              }}
            >
              시작하기 전에 딱 2가지 단계만 진행할게요.
              <br />
              먼저, 서비스 이용을 위해 약관에 동의해주세요.
            </p>
          </div>

          {/* 약관 동의 섹션 */}
          <div
            style={{
              alignSelf: "stretch",
              paddingLeft: isMobile ? "16px" : "16px",
              paddingRight: isMobile ? "16px" : "16px",
              paddingTop: "12px",
              paddingBottom: "12px",
              background: "white",
              borderRadius: 16,
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 8,
              display: "flex",
            }}
          >
            {/* 전체 동의 */}
            <div
              style={{
                alignSelf: "stretch",
                paddingBottom: "8px",
                borderBottom: "1px #E5E5E5 solid",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: 12,
                display: "flex",
              }}
            >
              <div
                style={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 8,
                  display: "flex",
                  cursor: "pointer",
                }}
                onClick={() => {
                  toggleAllActive();
                }}
              >
                <S.IconBox $isActive={isAllActive}>
                  <IconCircleCheck />
                </S.IconBox>
                <span
                  style={{
                    color: "#525252",
                    fontSize: isMobile ? 16 : 16,
                    fontFamily: "Pretendard",
                    fontWeight: "500",
                    lineHeight: isMobile ? 1 : 1,
                  }}
                >
                  루틴케어 이용약관 전체 동의
                </span>
              </div>
            </div>

            {/* 개별 약관들 */}
            {TERMS.map((term) => {
              return (
                <div
                  key={term.id}
                  style={{
                    alignSelf: "stretch",
                    height: 36,
                    borderRadius: 8,
                    justifyContent: "space-between",
                    alignItems: "center",
                    display: "inline-flex",
                  }}
                >
                  <div
                    style={{
                      justifyContent: "flex-start",
                      alignItems: "center",
                      gap: "10px",
                      display: "flex",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleActive(term.id)}
                  >
                    <S.IconBox $isActive={activeMap[term.id]}>
                      <IconCheck />
                    </S.IconBox>
                    <span
                      style={{
                        color: "#737373",
                        fontSize: isMobile ? 12 : 14,
                        fontFamily: "Pretendard Variable",
                        fontWeight: "500",
                      }}
                    >
                      {term.label}
                    </span>
                  </div>
                  <div
                    style={{ position: "relative", cursor: "pointer" }}
                    onClick={() => {
                      setSelectedTermId(term.id);
                      setModalShow(true);
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.22032 5.22032C8.36094 5.07987 8.55157 5.00098 8.75032 5.00098C8.94907 5.00098 9.13969 5.07987 9.28032 5.22032L13.5303 9.47032C13.6708 9.61094 13.7497 9.80157 13.7497 10.0003C13.7497 10.1991 13.6708 10.3897 13.5303 10.5303L9.28032 14.7803C9.13814 14.9128 8.9501 14.9849 8.75579 14.9815C8.56149 14.9781 8.37611 14.8994 8.2387 14.7619C8.10128 14.6245 8.02257 14.4391 8.01914 14.2448C8.01571 14.0505 8.08784 13.8625 8.22032 13.7203L11.9403 10.0003L8.22032 6.28032C8.07987 6.13969 8.00098 5.94907 8.00098 5.75032C8.00098 5.55157 8.07987 5.36094 8.22032 5.22032Z"
                        fill="#D4D4D4"
                      />
                    </svg>
                  </div>
                  {modalShow && (
                    <ModalRegister
                      title={selectedTerm?.label}
                      text={<Terms termsId={selectedTerm?.id ?? ""} />}
                      toggleActive={() => setActiveTrue(selectedTerm?.id ?? "")}
                      setModalShow={setModalShow}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    </PageContainer>
  );
};
